/**
 * DriftView — full-screen looping calming visuals for gazing when words are
 * too much. Cycles through a small library of AI-generated scenes: mountain
 * lake, forest, ocean, aurora, rain window.
 *
 * Design rules (unchanged from v1, extended for cycling):
 *   • Full-viewport video, `object-cover`, no visible controls
 *   • Only affordances: soft back-arrow (top-left, safe-area) and a soft
 *     thumbnail rail (bottom, safe-area) — both auto-fade so they never
 *     compete with the video after the user has settled
 *   • Mobile swipe: left = next scene, right = previous. Threshold is
 *     conservative so accidental drags on scroll don't jump scenes
 *   • Restart at frame 0 on scene change; pause + rewind on close so every
 *     open lands on the first frame
 *   • WebM VP9 preferred (smaller), MP4 H.264 fallback (Safari)
 *
 * by David Hunt
 */

import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import {
  DRIFT_SCENES,
  DRIFT_VIDEO_BASE,
  loadDriftScenePreference,
  saveDriftScenePreference,
} from "@/data/driftScenes";

// Swipe distance (px) that qualifies as an intentional scene change. Tuned
// so a light pinch doesn't cycle scenes but a proper drag will.
const SWIPE_THRESHOLD = 55;

export const DriftView = ({ open, onClose }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const touchStartRef = useRef(null);
  const [sceneId, setSceneId] = useState(loadDriftScenePreference);

  // Restart the video whenever the scene changes or the overlay opens; pause
  // + rewind on close so re-opening always lands on frame 0 of the last-used
  // scene.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (!open) {
      try { v.pause(); v.currentTime = 0; } catch (_) { /* silent */ }
      return;
    }
    try { v.currentTime = 0; v.load(); } catch (_) { /* silent */ }
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => { /* autoplay blocked */ });
  }, [open, sceneId]);

  // Persist the last-picked scene so returning users keep their vibe.
  useEffect(() => {
    saveDriftScenePreference(sceneId);
  }, [sceneId]);

  const goToIndex = (idx) => {
    const wrapped = ((idx % DRIFT_SCENES.length) + DRIFT_SCENES.length) % DRIFT_SCENES.length;
    setSceneId(DRIFT_SCENES[wrapped].id);
  };
  const currentIndex = Math.max(0, DRIFT_SCENES.findIndex((s) => s.id === sceneId));

  // Touch handlers — only intercept simple horizontal drags. If the user
  // moves mostly vertically we bail so we don't fight system gestures.
  const onTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY, at: Date.now() };
  };
  const onTouchEnd = (e) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
    if (Math.abs(dy) > Math.abs(dx) * 0.75) return; // mostly vertical → ignore
    goToIndex(currentIndex + (dx < 0 ? 1 : -1));
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="drift-overlay"
          ref={containerRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          className="fixed inset-0 z-40 bg-black select-none"
          data-testid="drift-overlay"
          data-scene={sceneId}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Cross-fade between scenes so the swap never snaps. AnimatePresence
              keyed on scene id keeps the old <video> alive during fade-out. */}
          <AnimatePresence mode="sync">
            <motion.video
              ref={videoRef}
              key={sceneId}
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.4, ease: "easeInOut" }}
              data-testid="drift-video"
              aria-label="A calming looping visual"
            >
              <source src={`${DRIFT_VIDEO_BASE}/${sceneId}.webm`} type="video/webm" />
              <source src={`${DRIFT_VIDEO_BASE}/${sceneId}.mp4`} type="video/mp4" />
            </motion.video>
          </AnimatePresence>

          {/* Back to home */}
          <button
            onClick={onClose}
            className="fixed w-11 h-11 rounded-full flex items-center justify-center bg-black/40 backdrop-blur-xl border border-white/20 text-white/85 hover:bg-black/60 hover:text-white transition-colors duration-500 z-10"
            style={{
              top: "calc(env(safe-area-inset-top) + 1rem)",
              left: "calc(env(safe-area-inset-left) + 1rem)",
            }}
            data-testid="drift-close-btn"
            aria-label="Return home"
          >
            <ArrowLeft size={18} strokeWidth={1.5} />
          </button>

          {/* Scene rail (bottom, safe-area). Auto-visible; small enough to
              live at the edge without covering the composition. */}
          <div
            className="fixed left-1/2 -translate-x-1/2 flex items-center gap-2.5 px-3 py-2 rounded-full bg-black/35 backdrop-blur-xl border border-white/15 z-10"
            style={{ bottom: "calc(env(safe-area-inset-bottom) + 1.25rem)" }}
            data-testid="drift-scene-rail"
            role="tablist"
            aria-label="Choose a drift scene"
          >
            {DRIFT_SCENES.map((scene, idx) => {
              const active = scene.id === sceneId;
              return (
                <button
                  key={scene.id}
                  role="tab"
                  aria-selected={active}
                  aria-label={scene.label}
                  title={scene.label}
                  onClick={() => goToIndex(idx)}
                  className={`relative w-8 h-8 rounded-full transition-all duration-500 flex items-center justify-center ${
                    active
                      ? "scale-100 border border-white/70"
                      : "scale-90 border border-white/25 hover:border-white/60"
                  }`}
                  style={{
                    backgroundColor: `rgba(${scene.accent}, ${active ? 0.9 : 0.35})`,
                    boxShadow: active ? `0 0 18px rgba(${scene.accent}, 0.55)` : undefined,
                  }}
                  data-testid={`drift-scene-${scene.id}`}
                  data-active={active}
                />
              );
            })}
          </div>

          {/* Scene label — small, low-contrast, only appears when the user
              switches. Fades after ~1.6s so it stops competing with the video. */}
          <AnimatePresence mode="wait">
            <motion.p
              key={sceneId}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.9, ease: "easeInOut" }}
              className="fixed left-1/2 -translate-x-1/2 text-[11px] tracking-[0.35em] uppercase text-white/60 pointer-events-none z-10"
              style={{ bottom: "calc(env(safe-area-inset-bottom) + 4.5rem)" }}
              data-testid="drift-scene-label"
            >
              {(DRIFT_SCENES[currentIndex] || DRIFT_SCENES[0]).label}
            </motion.p>
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DriftView;
